<template>
  <div class="warehouse-location-select">
    <el-input :value="displayText" readonly :disabled="disabled" :size="size" placeholder="请选择仓库/库区/库位">
      <template slot="append">
        <el-button :disabled="disabled" :size="size" @click="openPicker">选择</el-button>
        <el-button v-if="clearable" :disabled="disabled || !selectedLocationId" :size="size" icon="el-icon-close" @click="clearValue" />
      </template>
    </el-input>
    <el-dialog title="选择仓库/库区/库位" :visible.sync="pickerVisible" width="520px" append-to-body :close-on-click-modal="false">
      <el-tree
        ref="pickerTree"
        :data="treeData"
        node-key="id"
        default-expand-all
        :expand-on-click-node="false"
        highlight-current
        :props="{ children: 'children', label: 'label' }"
        @node-click="handleNodeClick"
      />
      <div class="picker-tip">请选择库位；未配置库位的仓库可直接选择仓库</div>
      <span slot="footer">
        <el-button @click="pickerVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmPick">确定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import request from '../api/request'

export default {
  name: 'WarehouseLocationSelect',
  props: {
    value: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    clearable: { type: Boolean, default: true },
    size: { type: String, default: '' }
  },
  data() {
    return {
      loading: false,
      wareHouseList: [],
      areaListByWarehouse: {},
      nodeMap: {},
      treeData: [],
      pickerVisible: false,
      tempSelectedLocationId: '',
      selectedWareHouseId: '',
      selectedAreaId: '',
      selectedLocationId: ''
    }
  },
  computed: {
    selectedPath() {
      if (!this.selectedLocationId) return ''
      if (this.selectedLocationId.startsWith('WH_')) {
        const warehouse = this.wareHouseList.find(item => item.wareHouseId === this.selectedWareHouseId)
        return warehouse ? warehouse.wareHouseName : ''
      }
      const location = this.nodeMap[this.selectedLocationId]
      if (!location || location.type !== '1') return ''
      const area = this.nodeMap[location.parentId] || {}
      const warehouse = this.wareHouseList.find(item => item.wareHouseId === location.wareHouseId) || {}
      if (!warehouse.wareHouseName || !area.wareAreaName || !location.wareAreaName) return ''
      return warehouse.wareHouseName + '/' + area.wareAreaName + '/' + location.wareAreaName
    },
    displayText() {
      return this.selectedPath ? this.selectedPath.replace(/\//g, ' / ') : ''
    }
  },
  watch: {
    value: {
      handler(val) {
        if (!this.loading) this.syncFromValue(val)
      },
      immediate: true
    }
  },
  created() {
    this.loadOptions()
  },
  methods: {
    loadOptions() {
      this.loading = true
      request.post('/base/wareHouse/list', { currentPage: 1, pageSize: 500 }).then(res => {
        const rows = (res.data && res.data.list) || []
        this.wareHouseList = rows.filter(item => String(item.isenabled || '0') === '0')
        return Promise.all(this.wareHouseList.map(item => request.get('/base/wareArea/list/' + item.wareHouseId).then(areaRes => areaRes.data || []).catch(() => [])))
      }).then(results => {
        const areaListByWarehouse = {}
        const nodeMap = {}
        this.wareHouseList.forEach((warehouse, idx) => {
          const list = results[idx] || []
          areaListByWarehouse[warehouse.wareHouseId] = list
          list.forEach(node => { nodeMap[node.wareAreaId] = node })
        })
        this.areaListByWarehouse = areaListByWarehouse
        this.nodeMap = nodeMap
        this.treeData = this.buildTreeData()
        this.syncFromValue(this.value)
      }).catch(() => {
        this.wareHouseList = []
        this.areaListByWarehouse = {}
        this.nodeMap = {}
        this.treeData = []
      }).finally(() => { this.loading = false })
    },
    buildTreeData() {
      return this.wareHouseList.map(warehouse => {
        const list = this.areaListByWarehouse[warehouse.wareHouseId] || []
        const byParent = {}
        list.forEach(item => {
          const pid = item.parentId || '__root__'
          if (!byParent[pid]) byParent[pid] = []
          byParent[pid].push(item)
        })
        const buildChildren = parentId => (byParent[parentId] || []).map(item => ({
          id: item.wareAreaId,
          wareHouseId: item.wareHouseId,
          wareAreaId: item.wareAreaId,
          wareAreaName: item.wareAreaName,
          type: item.type,
          parentId: item.parentId || '',
          label: (item.type === '0' ? '[库区] ' : '[库位] ') + item.wareAreaName,
          children: buildChildren(item.wareAreaId)
        }))
        return {
          id: 'WH_' + warehouse.wareHouseId,
          wareHouseId: warehouse.wareHouseId,
          type: 'WH',
          label: '[仓库] ' + warehouse.wareHouseName,
          children: buildChildren('__root__')
        }
      })
    },
    syncFromValue(val) {
      if (!val) {
        this.selectedWareHouseId = ''
        this.selectedAreaId = ''
        this.selectedLocationId = ''
        return
      }
      const normalized = String(val).replace(/\s+/g, '')
      if (normalized.indexOf('/') > -1) {
        const parts = normalized.split('/').filter(Boolean)
        if (parts.length === 3) {
          const warehouse = this.wareHouseList.find(item => item.wareHouseName === parts[0])
          if (warehouse) {
            const area = Object.values(this.nodeMap).find(node => node && node.type === '0' && node.wareHouseId === warehouse.wareHouseId && node.wareAreaName === parts[1])
            const location = Object.values(this.nodeMap).find(node => node && node.type === '1' && node.wareHouseId === warehouse.wareHouseId && node.parentId === (area && area.wareAreaId) && node.wareAreaName === parts[2])
            if (location) {
              this.selectedWareHouseId = warehouse.wareHouseId
              this.selectedAreaId = location.parentId || ''
              this.selectedLocationId = location.wareAreaId
              return
            }
          }
        }
      }
      const node = this.nodeMap[val]
      if (node && node.type === '1') {
        this.selectedWareHouseId = node.wareHouseId
        this.selectedAreaId = node.parentId || ''
        this.selectedLocationId = node.wareAreaId
        return
      }
      if (node && node.type === '0') {
        this.selectedWareHouseId = node.wareHouseId
        this.selectedAreaId = node.wareAreaId
        this.selectedLocationId = ''
        return
      }
      const warehouse = this.wareHouseList.find(item => item.wareHouseId === val)
      if (warehouse) {
        const location = this.pickFirstLocation(warehouse.wareHouseId)
        if (location) {
          this.selectedWareHouseId = warehouse.wareHouseId
          this.selectedAreaId = location.parentId || ''
          this.selectedLocationId = location.wareAreaId
          this.$nextTick(() => {
            if (this.selectedPath && this.selectedPath !== val) this.emitValue()
          })
          return
        }
        this.selectedWareHouseId = warehouse.wareHouseId
        this.selectedAreaId = ''
        this.selectedLocationId = this.hasLocation(warehouse.wareHouseId) ? '' : 'WH_' + warehouse.wareHouseId
      }
    },
    hasLocation(wareHouseId) {
      return (this.areaListByWarehouse[wareHouseId] || []).some(node => node && node.type === '1')
    },
    pickFirstLocation(wareHouseId) {
      const list = this.areaListByWarehouse[wareHouseId] || []
      const area = list.find(node => node && node.type === '0')
      if (!area) return null
      return list.find(node => node && node.type === '1' && node.parentId === area.wareAreaId) || null
    },
    openPicker() {
      this.tempSelectedLocationId = this.selectedLocationId || ''
      this.pickerVisible = true
      this.$nextTick(() => {
        if (this.tempSelectedLocationId && this.$refs.pickerTree) this.$refs.pickerTree.setCurrentKey(this.tempSelectedLocationId)
      })
    },
    handleNodeClick(node) {
      if (node.type !== '1' && !(node.type === 'WH' && !this.hasLocation(node.wareHouseId))) return
      this.tempSelectedLocationId = node.id
    },
    confirmPick() {
      if (!this.tempSelectedLocationId) {
        this.$message.warning('请选择库位或未配置库位的仓库')
        return
      }
      if (this.tempSelectedLocationId.startsWith('WH_')) {
        const wareHouseId = this.tempSelectedLocationId.slice(3)
        if (!this.wareHouseList.some(item => item.wareHouseId === wareHouseId) || this.hasLocation(wareHouseId)) {
          this.$message.warning('请选择库位')
          return
        }
        this.selectedWareHouseId = wareHouseId
        this.selectedAreaId = ''
        this.selectedLocationId = this.tempSelectedLocationId
        this.emitValue()
        this.pickerVisible = false
        return
      }
      const location = this.nodeMap[this.tempSelectedLocationId]
      if (!location || location.type !== '1') {
        this.$message.warning('请选择库位')
        return
      }
      this.selectedWareHouseId = location.wareHouseId
      this.selectedAreaId = location.parentId || ''
      this.selectedLocationId = location.wareAreaId
      this.emitValue()
      this.pickerVisible = false
    },
    clearValue() {
      this.selectedWareHouseId = ''
      this.selectedAreaId = ''
      this.selectedLocationId = ''
      this.tempSelectedLocationId = ''
      this.emitValue()
    },
    emitValue() {
      const value = this.selectedLocationId.startsWith('WH_') ? this.selectedWareHouseId : (this.selectedPath || '')
      this.$emit('input', value)
      this.$emit('change', value)
    }
  }
}
</script>

<style scoped>
.warehouse-location-select { width: 100%; }
.picker-tip { margin-top: 8px; color: #909399; font-size: 12px; }
</style>
